// @ts-nocheck
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import { useCookie } from "../../../hooks/useCookie";
import { useSelectedPlace } from "../../../hooks/place/useSelectedPlace";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import {
  ALL_DAYS,
  CLOSED_ALL_WEEK,
  COPY_TARGET_DAYS,
  DEFAULT_VALUES,
} from "../../../helpers/constants";
import { openingHoursSchema } from "./schema";
import type {
  ApiOpeningHours,
  CleanedOpeningHours,
  CopyTarget,
  DayFormValue,
  OpeningHoursFormValues,
} from "../../../helpers/types";
import { cleanOpeningHoursData, cloneDayValue, parseOpeningHoursResponse } from "./utils";

interface Options {
  visible: boolean;
  placeId?: string;
  onClose: () => void;
}

export function useOpeningHours({ visible, placeId, onClose }: Options) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const form = useForm<OpeningHoursFormValues>({
    resolver: yupResolver(openingHoursSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const { setValue, getValues } = form;

  const exceptions = useFieldArray({
    control: form.control,
    name: "exceptions",
  });

  // Load the current opening hours when the modal opens
  useEffect(() => {
    if (!visible || !placeId) return;

    apiService
      .MakeGetRequest(`places/${placeId}/openingHours`, token)
      .then((resp: ApiOpeningHours) => {
        const { days, exceptions: loaded } = parseOpeningHoursResponse(resp);
        ALL_DAYS.forEach((day) => {
          const value = days[day];
          if (!value) return;
          setValue(day, value);
          if (value.status === "custom") {
            setValue(`${day}.ranges`, value.ranges ?? []);
          }
        });
        if (loaded) setValue("exceptions", loaded);
      })
      .catch((err) => {
        console.error(err);
        ALL_DAYS.forEach((day) => setValue(day, CLOSED_ALL_WEEK[day]!));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, placeId]);

  // Admins create via POST on the place, place admins update "ours"
  const saveMutation = useMutation((data: CleanedOpeningHours) =>
    isAdmin
      ? apiService.MakePostRequest(`places/${placeId}/openingHours`, data, token)
      : apiService.MakePutRequest(
          `places/ours/openingHours?placeId=${selectedPlaceId}`,
          data,
          token,
        ),
  );

  const onSubmit = form.handleSubmit((values) =>
    saveMutation.mutate(cleanOpeningHoursData(values), {
      onSuccess() {
        toastSuccess(general.fr.message.openingHrAdded);
        queryClient.invalidateQueries("getPlace");
        onClose();
      },
      onError() {
        toastError(general.fr.message.operationFailed);
      },
    }),
  );

  const copyTo = (target: CopyTarget, value: DayFormValue) => {
    COPY_TARGET_DAYS[target].forEach((day) =>
      setValue(day, cloneDayValue(value)),
    );
  };

  return {
    form,
    exceptions,
    onSubmit,
    copyTo,
    isSaving: saveMutation.isLoading,
    getDay: (day: Parameters<typeof getValues>[0]) => getValues(day),
  };
}